/**
 * Helpers for custom font file parsing (client-side validation before POST).
 * Persistence: services/customFontsApi.js (GET / POST / DELETE).
 */

import { DEFAULT_INLINE_EDITOR_FONT_FAMILIES } from "./CommonFroalaConfig";

/** Allowed file extensions → CSS font format() value. Only .ttf and .otf are accepted. */
export const CUSTOM_FONT_EXTENSION_TO_FORMAT = {
  ttf: "truetype",
  otf: "opentype",
};

/** Shown in UI hints and error toasts. */
export const CUSTOM_FONT_SUPPORTED_FORMATS_LABEL = ".TTF, .OTF";

const CUSTOM_FONT_FORMAT_TO_EXTENSION = {
  truetype: "ttf",
  opentype: "otf",
};

export function isCustomFontFileSupported(file) {
  const ext = file?.name?.split(".").pop()?.toLowerCase() ?? "";
  return Boolean(CUSTOM_FONT_EXTENSION_TO_FORMAT[ext]);
}

export function getCustomFontUnsupportedFormatMessage(fileName = "File") {
  return `${fileName}: supported formats are ${CUSTOM_FONT_SUPPORTED_FORMATS_LABEL} only.`;
}

/** Built-in font names (same keys as Froala default map) — cannot be removed as “custom”. */
export function getBuiltInFontDisplayNames() {
  return Object.keys(DEFAULT_INLINE_EDITOR_FONT_FAMILIES);
}

/**
 * Builds a safe CSS @font-face family name token and display label from a File.
 */
export function deriveLabelAndFamilyNameFromFile(file) {
  const baseName =
    file.name
      .replace(/\.[^.]+$/i, "")
      .replace(/[^\w\s-]/g, "")
      .trim() || "CustomFont";
  const familyStem = baseName.replace(/\s+/g, "") || "CustomFont";
  return { displayLabel: baseName, familyName: familyStem };
}

/** Display filename with extension for saved custom font records. */
export function getCustomFontFileNameWithExtension(record) {
  const fileName = String(record?.fileName ?? "").trim();
  if (fileName) return fileName;

  const label = String(record?.label ?? "").trim();
  if (!label) return "";

  if (/\.(ttf|otf)$/i.test(label)) {
    return label;
  }

  const ext =
    CUSTOM_FONT_FORMAT_TO_EXTENSION[String(record?.format ?? "").toLowerCase()];
  return ext ? `${label}.${ext}` : label;
}
